import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  IconButton,
  Chip,
  Tooltip,
} from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { format, startOfWeek, addDays, isSameDay, addWeeks, subWeeks } from 'date-fns';
import { ja } from 'date-fns/locale';
import AdvancedShiftEditDialog from './AdvancedShiftEditDialog';
import { ShiftDataManager } from './ShiftDataManager';
import { 
  ExtendedStaffShiftData,
  APPOINTMENT_CONFIG,
  AvailabilityCalculator
} from '../../types/extended-shift';

// サイトコントローラー用のシンプルなシフトデータ構造
export interface StaffShiftData {
  id: string;
  staffId: string;
  date: string; // 'YYYY-MM-DD'
  workingHours: {
    start: string; // 'HH:mm'
    end: string;   // 'HH:mm'
  } | null; // null = 休み
  createdAt: string;
  updatedAt: string;
}

interface Staff {
  id: string;
  name: string;
  position: string;
  status: string;
}

interface SimpleShiftBoardProps {
  staffList: Staff[];
}

const SimpleShiftBoard: React.FC<SimpleShiftBoardProps> = ({ staffList }) => {
  
  // 状態管理（拡張データ対応）
  const [currentWeek, setCurrentWeek] = useState(() => startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [extendedShiftData, setExtendedShiftData] = useState<ExtendedStaffShiftData[]>([]);
  const [editingShift, setEditingShift] = useState<{
    staff: Staff;
    date: string;
    current?: ExtendedStaffShiftData;
  } | null>(null);

  // データ読み込み（拡張データ対応）
  useEffect(() => {
    const loadExtendedShiftData = async () => {
      try {
        console.log('=== Loading Extended Shift Data ===');
        
        // localStorage状況確認
        const basicData = localStorage.getItem('salonhub_staff_shifts');
        const extendedData = localStorage.getItem('salonhub_extended_staff_shifts');
        console.log('localStorage basic data:', basicData ? JSON.parse(basicData) : null);
        console.log('localStorage extended data:', extendedData ? JSON.parse(extendedData) : null);
        
        const data = await ShiftDataManager.getAllExtendedShifts();
        console.log('Loaded data:', data);
        console.log('Data length:', data.length);
        setExtendedShiftData(data);
      } catch (error) {
        console.error('拡張シフトデータの読み込みに失敗:', error);
      }
    };
    
    loadExtendedShiftData();
  }, []);

  // データ保存
  const saveExtendedShiftData = async (data: ExtendedStaffShiftData[]) => {
    try {
      await ShiftDataManager.saveAllExtendedShifts(data);
      setExtendedShiftData(data);
    } catch (error) {
      console.error('拡張シフトデータの保存に失敗:', error);
    }
  };

  // 週の日付配列を生成
  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(currentWeek, i));

  // 特定のスタッフ・日付の拡張シフトデータを取得
  const getExtendedShiftForStaffAndDate = (staffId: string, date: Date): ExtendedStaffShiftData | null => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return extendedShiftData.find(s => s.staffId === staffId && s.date === dateStr) || null;
  };

  // シフト編集開始
  const handleEditShift = (staffId: string, date: Date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    const staff = staffList.find(s => s.id === staffId);
    const currentShift = getExtendedShiftForStaffAndDate(staffId, date);
    
    // デバッグログ追加
    console.log('=== handleEditShift DEBUG ===');
    console.log('staffId:', staffId);
    console.log('dateStr:', dateStr);
    console.log('currentShift:', currentShift);
    console.log('extendedShiftData.length:', extendedShiftData.length);
    console.log('extendedShiftData:', extendedShiftData);
    
    if (staff) {
      setEditingShift({
        staff,
        date: dateStr,
        current: currentShift || undefined, // null を undefined に変換
      });
    }
  };

  // シフト保存
  const handleSaveExtendedShift = async (shiftData: ExtendedStaffShiftData) => {
    const updatedData = editingShift?.current
      ? extendedShiftData.map(s => s.id === shiftData.id ? shiftData : s)
      : [...extendedShiftData, shiftData];

    await saveExtendedShiftData(updatedData);
  };

  // 週の変更
  const handlePreviousWeek = () => setCurrentWeek(prev => subWeeks(prev, 1));
  const handleNextWeek = () => setCurrentWeek(prev => addWeeks(prev, 1));

  // 新構造対応の拡張シフト表示用セルレンダリング
  const renderExtendedShiftCell = (staff: Staff, date: Date) => {
    const shift = getExtendedShiftForStaffAndDate(staff.id, date);
    const isToday = isSameDay(date, new Date());

    // 背景色とコンテンツの決定
    let bgcolor = 'background.paper';
    let content = null;
    let tooltipContent = 'クリックして設定';

    if (shift) {
      if (shift.shiftType === 'work' && shift.workDetails?.baseWorkingHours) {
        bgcolor = 'success.50';
        const workDetails = shift.workDetails;
        const appointments = workDetails.appointments || [];
        
        // 予定タイプ別の集計
        const appointmentSummary = AvailabilityCalculator.summarizeAppointments(appointments);
        const totalAppointments = appointments.length;
        
        // 空き時間計算
        let availabilityInfo = workDetails.availabilityInfo;
        if (!availabilityInfo) {
          availabilityInfo = AvailabilityCalculator.calculateAvailability(
            workDetails.baseWorkingHours,
            appointments
          );
        }
        
        content = (
          <Box sx={{ textAlign: 'center', width: '100%', px: 0.5 }}>
            {/* 基本勤務時間 */}
            <Typography variant="caption" sx={{ fontSize: '0.75rem', fontWeight: 'bold', display: 'block' }}>
              {workDetails.baseWorkingHours.start}-{workDetails.baseWorkingHours.end}
            </Typography>
            
            {/* 予定表示 */}
            {totalAppointments > 0 ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                {Object.entries(appointmentSummary).map(([type, count]) => {
                  if (count === 0) return null;
                  const config = APPOINTMENT_CONFIG[type as keyof typeof APPOINTMENT_CONFIG];
                  return (
                    <Chip 
                      key={type}
                      label={`${config.icon}${count}`}
                      size="small"
                      sx={{ 
                        height: 18, 
                        fontSize: '0.6rem',
                        bgcolor: config.bgColor,
                        color: config.color,
                        '& .MuiChip-label': { px: 0.5 }
                      }}
                    />
                  );
                })}
              </Box>
            ) : (
              <Typography variant="caption" sx={{ fontSize: '0.6rem', color: 'text.secondary', mt: 0.5, display: 'block' }}>
                予定なし
              </Typography>
            )}
            
            {/* 空き時間表示 */}
            <Typography variant="caption" sx={{ 
              fontSize: '0.6rem', 
              color: availabilityInfo.availabilityStatus === 'available' ? 'success.main' :
                     availabilityInfo.availabilityStatus === 'busy' ? 'error.main' : 'warning.main',
              display: 'block',
              fontWeight: 'medium',
              mt: 0.5
            }}>
              空き{Math.floor(availabilityInfo.availableMinutes / 60)}h
              {availabilityInfo.availableMinutes % 60 > 0 && `${availabilityInfo.availableMinutes % 60}m`}
            </Typography>
          </Box>
        );
        
        // ツールチップ内容の充実
        const appointmentDetails = Object.entries(appointmentSummary)
          .filter(([, count]) => count > 0)
          .map(([type, count]) => `${APPOINTMENT_CONFIG[type as keyof typeof APPOINTMENT_CONFIG].label}: ${count}件`)
          .join(', ');
          
        tooltipContent = `
勤務: ${workDetails.baseWorkingHours.start}-${workDetails.baseWorkingHours.end}
予定: ${appointmentDetails || 'なし'}
空き: ${Math.floor(availabilityInfo.availableMinutes / 60)}時間${availabilityInfo.availableMinutes % 60}分
        `.trim();
        
      } else if (shift.shiftType === 'work' && shift.workDetails?.schedules?.length) {
        // 旧構造での表示（後方互換性）
        bgcolor = 'warning.50';
        const schedules = shift.workDetails.schedules;
        const firstSchedule = schedules[0];
        
        content = (
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', fontWeight: 'medium', display: 'block' }}>
              {firstSchedule.startTime} - {firstSchedule.endTime}
            </Typography>
            {schedules.length > 1 && (
              <Chip 
                label={`${schedules.length}件`} 
                size="small" 
                color="warning" 
                sx={{ height: 16, fontSize: '0.6rem', mt: 0.5 }} 
              />
            )}
          </Box>
        );
        tooltipContent = `予定: ${schedules.length}件`;
        
      } else if (shift.shiftType === 'holiday') {
        bgcolor = 'grey.100';
        content = (
          <Box sx={{ textAlign: 'center' }}>
            <Typography sx={{ fontSize: '1.2rem', mb: 0.5 }}>
              🗓️
            </Typography>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
              休日
            </Typography>
            {shift.holidayInfo?.title && (
              <Typography variant="caption" sx={{ fontSize: '0.6rem', color: 'text.secondary', display: 'block' }}>
                {shift.holidayInfo.title}
              </Typography>
            )}
          </Box>
        );
        tooltipContent = shift.holidayInfo?.title ? `休日: ${shift.holidayInfo.title}` : '休日';
      }
    } else {
      // 未設定
      content = (
        <Box sx={{ textAlign: 'center' }}>
          <IconButton size="small" sx={{ opacity: 0.3 }}>
            <EditIcon fontSize="small" />
          </IconButton>
          <Typography variant="caption" sx={{ fontSize: '0.6rem', color: 'text.secondary', display: 'block' }}>
            未設定
          </Typography>
        </Box>
      );
    }

    return (
      <Tooltip 
        title={tooltipContent}
        placement="top"
        arrow
      >
        <Paper
          key={`${staff.id}-${format(date, 'yyyy-MM-dd')}`}
          elevation={0}
          sx={{
            p: 1,
            minHeight: 85,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            border: '2px solid',
            borderColor: isToday ? 'primary.main' : 'divider',
            bgcolor,
            cursor: 'pointer',
            transition: 'all 0.2s ease-in-out',
            position: 'relative',
            '&:hover': {
              bgcolor: 'action.hover',
              transform: 'translateY(-1px)',
              boxShadow: 2,
            },
          }}
          onClick={() => handleEditShift(staff.id, date)}
        >
          {content}
        </Paper>
      </Tooltip>
    );
  };

  return (
    <Box>
      {/* ヘッダー: 週ナビゲーション */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 'medium' }}>
          シフト管理 - {format(currentWeek, 'yyyy年MM月', { locale: ja })}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton onClick={handlePreviousWeek} size="small">
            <ChevronLeftIcon />
          </IconButton>
          <Typography variant="body2" sx={{ minWidth: '120px', textAlign: 'center' }}>
            {format(currentWeek, 'M/d')} - {format(addDays(currentWeek, 6), 'M/d')}
          </Typography>
          <IconButton onClick={handleNextWeek} size="small">
            <ChevronRightIcon />
          </IconButton>
        </Box>
      </Box>

      {/* メインテーブル */}
      <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
        {/* ヘッダー行: 日付 */}
        <Grid container sx={{ bgcolor: 'grey.50' }}>
          <Grid item xs={2}>
            <Box sx={{ p: 2, borderRight: '1px solid', borderColor: 'divider' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'medium' }}>
                スタッフ
              </Typography>
            </Box>
          </Grid>
          {weekDays.map((date) => (
            <Grid item xs={10/7} key={format(date, 'yyyy-MM-dd')}>
              <Box 
                sx={{ 
                  p: 1, 
                  textAlign: 'center',
                  borderRight: '1px solid',
                  borderColor: 'divider',
                  '&:last-child': { borderRight: 'none' }
                }}
              >
                <Typography variant="caption" sx={{ display: 'block', fontWeight: 'medium' }}>
                  {format(date, 'E', { locale: ja })}
                </Typography>
                <Typography variant="body2" sx={{ color: isSameDay(date, new Date()) ? 'primary.main' : 'text.primary' }}>
                  {format(date, 'd')}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>

        {/* データ行: スタッフ × 日付 */}
        {staffList.filter(staff => staff.status === 'active').map((staff) => (
          <Grid container key={staff.id} sx={{ borderTop: '1px solid', borderColor: 'divider' }}>
            <Grid item xs={2}>
              <Box sx={{ p: 2, borderRight: '1px solid', borderColor: 'divider' }}>
                <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                  {staff.name}
                </Typography>
              </Box>
            </Grid>
            {weekDays.map((date) => (
              <Grid item xs={10/7} key={format(date, 'yyyy-MM-dd')}>
                <Box sx={{ borderRight: '1px solid', borderColor: 'divider', '&:last-child': { borderRight: 'none' } }}>
                  {renderExtendedShiftCell(staff, date)}
                </Box>
              </Grid>
            ))}
          </Grid>
        ))}
      </Paper>

      {/* 拡張編集ダイアログ */}
      {editingShift && (
        <AdvancedShiftEditDialog
          open={!!editingShift}
          onClose={() => setEditingShift(null)}
          onSave={handleSaveExtendedShift}
          staff={editingShift.staff}
          date={editingShift.date}
          initialData={editingShift.current}
        />
      )}
    </Box>
  );
};

export default SimpleShiftBoard;